import { View, TableView, Dialog, dataFormatter } from 'web-mojo';
import { FirmwareUpdateList, FIRMWARE_UPDATE_BADGES, cancelQueuedUpdate } from '@portal/models/FirmwareUpdate.js';

const SKIP_MESSAGES = {
    no_identity: "This device hasn't reported its firmware identity yet — it will after its first check-in on OTA-capable firmware.",
    current: 'Device is already up to date.',
    no_build: "No published build matches this device's channel or version pin — check the firmware assignment.",
    offline: 'Device is offline, and no paired kiosk terminal is available to relay the update.',
    disabled: 'OTA updates are disabled for this device.',
    board_mismatch: "The resolved build doesn't match this device's board.",
    pending: 'An update was already dispatched recently (10-minute cooldown).',
    // `offline` (above) is the old-server hard stop — kept as the fallback when
    // the server can't queue; `already_queued` is the queue-aware skip for a
    // device that already has an update waiting to install on reconnect.
    already_queued: 'An update is already queued for this device — it will install when it reconnects.',
    // Relay-path skips — a docked lock controller reached through its kiosk terminal.
    terminal_offline: 'The paired kiosk terminal is offline — it must be connected to deliver firmware to this docked device. Retry once it reconnects.',
    terminal_busy: 'The paired kiosk terminal is busy staging another update — try again shortly.',
    ambiguous_pairing: 'This lock is paired to more than one terminal — fix the Lock Device pairing so exactly one terminal owns it, then retry.',
    bad_descriptor: 'The kiosk terminal rejected the update manifest (a server/terminal mismatch, not a device fault) — contact support.',
};

const STATUS_LABELS = {
    queued: 'Queued — will install when the device reconnects',
    sent: 'Dispatched — waiting for device…',
    started: 'Update started',
    downloading: 'Downloading…',
    downloaded: 'Downloaded',
    verifying: 'Verifying…',
    applying: 'Applying…',
    success: 'Update complete',
    failed: 'Update failed',
    rolled_back: 'Rolled back',
    cancelled: 'Cancelled',
};

const TERMINAL_STATUSES = ['success', 'failed', 'rolled_back'];

class DeviceFirmwareView extends View {
    constructor(options = {}) {
        super({
            className: 'device-firmware-view',
            ...options
        });
        this._activeAttempt = null;
        this._lastToastedAttempt = null;
        // Queued-OTA state: the live queued row (or null), whether the last
        // dry-run proved the server supports queueing, and the last progress
        // event (re-applied in onAfterRender so a re-render never drops progress).
        this._queued = null;
        this._canQueue = false;
        this._lastProgress = null;
        this._reconnectTimer = null;
    }

    async getTemplate() {
        return `
        <div class="p-3">
            <div class="row g-2 mb-2">
                <div class="col-4 col-md">
                    <div class="small text-muted">Board</div>
                    <div class="small fw-semibold">{{model.board|default('—')}}</div>
                </div>
                <div class="col-4 col-md">
                    <div class="small text-muted">Product</div>
                    <div class="small fw-semibold">{{model.product|default('—')}}</div>
                </div>
                <div class="col-4 col-md">
                    <div class="small text-muted">Customer</div>
                    <div class="small fw-semibold">{{model.customer|default('—')}}</div>
                </div>
                <div class="col-4 col-md">
                    <div class="small text-muted">Channel</div>
                    <div class="small fw-semibold">{{model.channel|default('—')}}</div>
                </div>
                <div class="col-4 col-md">
                    <div class="small text-muted">Running</div>
                    <div class="small fw-semibold font-monospace">{{model.metadata.version|default('unknown')}}</div>
                </div>
            </div>

            {{#lastOtaText|bool}}
            <div class="small text-muted mb-2"><i class="bi bi-clock-history me-1"></i>{{lastOtaText}}</div>
            {{/lastOtaText|bool}}

            <div class="mt-2 d-none" data-bind="ota-progress">
                <div class="d-flex justify-content-between align-items-center mb-1">
                    <span class="small fw-semibold" data-bind="ota-status-label"></span>
                    <span class="small text-muted" data-bind="ota-percent-label"></span>
                </div>
                <div class="progress" style="height: 8px;">
                    <div class="progress-bar progress-bar-striped progress-bar-animated" data-bind="ota-bar" style="width: 0%"></div>
                </div>
                <div class="small text-danger mt-1 d-none" data-bind="ota-error"></div>
            </div>

            {{#queuedText|bool}}
            <div class="alert alert-warning py-2 small d-flex justify-content-between align-items-center mb-3">
                <span><i class="bi bi-hourglass-split me-1"></i>{{queuedText}}</span>
                <button type="button" data-action="cancel-queued" class="btn btn-sm btn-outline-secondary ms-2 flex-shrink-0">Cancel</button>
            </div>
            {{/queuedText|bool}}

            <div class="d-flex gap-2 my-3">
                <button type="button" class="btn btn-sm btn-outline-secondary" data-action="check-update">
                    <i class="bi bi-search me-1"></i>Check for Update
                </button>
                <button type="button" class="btn btn-sm btn-primary" data-action="update-firmware">
                    <i class="bi bi-cloud-arrow-down me-1"></i>Update Firmware
                </button>
            </div>

            <div class="mb-2 fw-bold small text-uppercase text-muted">Update History</div>
            <div class="table-responsive" data-container="attempts-view"></div>
        </div>
        `;
    }

    async onInit() {
        this.attemptsView = new TableView({
            collection: new FirmwareUpdateList({
                params: { device: this.model.get('id'), size: 10 }
            }),
            containerId: 'attempts-view',
            fetchOnMount: true,
            hideActivePillNames: ['device'],
            clickAction: 'none',
            fontSize: 'sm',
            columns: [
                { key: 'created', label: 'When', formatter: 'relative' },
                {
                    key: 'to_version',
                    label: 'Update',
                    // from_version/to_version are device-reported (audit #293 H1) — escape before interpolating into HTML
                    formatter: (val, row) => `<span class="font-monospace small">${dataFormatter.escapeHtml(row?.from_version || '?')} <i class="bi bi-arrow-right"></i> ${dataFormatter.escapeHtml(row?.to_version || '?')}</span>`,
                },
                {
                    key: 'status',
                    label: 'Status',
                    formatter: FIRMWARE_UPDATE_BADGES,
                },
                { key: 'percent', label: '%', formatter: (val) => (val === null || val === undefined) ? '' : `${val}%` },
                { key: 'source', label: 'Source', formatter: "default('—')" },
                // Device-reported OTA error (audit #293 H1) — escape, not raw HTML. Note: the same
                // device error/stage strings also reach DeviceHistory.metadata server-side; if a
                // history-metadata surface is ever rendered, escape them there too.
                { key: 'error', label: 'Error', formatter: (val) => (val ? dataFormatter.escapeHtml(val) : '') },
            ]
        });
        this.addChild(this.attemptsView);

        // Live progress off the group topic relay; a differing attempt id means a
        // newer dispatch superseded the one on screen — adopt it (no superseded
        // event is ever published live).
        this._onOtaProgress = (e) => {
            if (!e || e.device_id !== this.model.get('id')) return;
            if (e.attempt && e.attempt !== this._activeAttempt) {
                this._activeAttempt = e.attempt;
            }
            // Auto-dispatch reflection: a waiting queued intent that fires shows up
            // as a `sent`/`started` progress event. Clear the banner and re-render —
            // synchronously (no await), so an event burst can't race an in-flight
            // render; onAfterRender re-applies _lastProgress to heal the swap.
            if (this._queued && (e.status === 'sent' || e.status === 'started')) {
                this._queued = null;
                this.render();
            }
            this._lastProgress = e;
            this._updateProgress(e);
        };
        this.getApp().events.on('device:ota_progress', this._onOtaProgress);

        // Queued-state banner: fetch the live queued row (if any) as its own
        // 1-row filtered list — robust against pagination, decoupled from the
        // attempts table. Non-blocking: DeviceView mounts this view on every panel
        // open and renders children sequentially, so awaiting here would delay the
        // whole panel. A failed fetch degrades to no banner; re-render only if
        // something is actually queued.
        this._loadQueued().then(() => { if (this._queued) this.render(); });

        // Reconnect refresh: some queued rows close out server-side with no OTA
        // progress event — a reconnect that resolves to `current`/`no_build`/
        // `disabled` cancels the intent silently. When our device comes online and
        // a banner is still up, re-fetch queued state after a beat (the server
        // needs a moment to process the reconnect). Also backstops a missed `sent`
        // event. Mirrors DeviceView's device:state_changed listener.
        this._onDeviceStateChanged = (event) => {
            if (event?.device_id !== this.model.get('id')) return;
            if (event.is_online === true && this._queued) {
                if (this._reconnectTimer) clearTimeout(this._reconnectTimer);
                this._reconnectTimer = setTimeout(() => {
                    this._reconnectTimer = null;
                    this._refreshQueued();
                }, 1500);
            }
        };
        this.getApp().events.on('device:state_changed', this._onDeviceStateChanged);
    }

    async onBeforeRender() {
        const ota = this.model.get('metadata')?.ota;
        if (!ota) {
            this.lastOtaText = '';
        } else if (ota.last_result) {
            const when = ota.finished_at ? ` on ${new Date(ota.finished_at * 1000).toLocaleString()}` : '';
            this.lastOtaText = `Last update: ${ota.last_result} → v${ota.last_version || '?'}${when}`;
        } else if (ota.sent_version) {
            const when = ota.sent_at ? ` at ${new Date(ota.sent_at * 1000).toLocaleString()}` : '';
            this.lastOtaText = `Dispatch open: v${ota.sent_version} sent${when} via ${ota.source || 'portal'}`;
            this._activeAttempt = this._activeAttempt || ota.attempt || null;
        } else {
            this.lastOtaText = '';
        }

        // Queued-OTA banner text. "currently" is load-bearing: the target build
        // resolves at dispatch time (the assignment may move before the device
        // reconnects), so the version shown is an estimate, never a promise.
        // Double-brace interpolation in the template escapes to_version.
        if (this._queued) {
            const when = dataFormatter.relative(this._queued.get('created')) || 'recently';
            const ver = this._queued.get('to_version') || '?';
            this.queuedText = `Queued ${when}: the newest matching build (currently v${ver}) will install when the device reconnects.`;
        } else {
            this.queuedText = '';
        }
    }

    async _dryRun() {
        const app = this.getApp();
        app.modal.loading('Checking for updates...');
        let response;
        try {
            response = await app.rest.POST('/api/firmware/ota/trigger', {
                device: this.model.get('id'),
                // Scope-only: names the group a member-tier manage_firmware
                // grant lives in so requires_perms can resolve it. The device
                // id remains the target. Global holders ignore it.
                group: this.model.get('group.id'),
                dry_run: true,
                // Ask the server to report `would_queue` for an offline target.
                // Without it the server never returns would_queue and every queue
                // branch below is dead. Safe to send blind on a dry-run: an old
                // server ignores the unknown field (mojo REST standard behavior).
                queue: true,
            });
        } finally {
            app.modal.hideLoading();
        }
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to check for updates');
            return null;
        }
        const data = response.data.data;
        // Capability probe: a queue-aware server always includes a `queued` count
        // in the envelope when the flag is honored. Its absence means an old
        // server — so live dispatches never send `queue: true` (see the trigger
        // calls), and the offline path stays exactly today's hard stop.
        this._canQueue = !!data && ('queued' in data);
        const result = data?.results?.[0];
        if (!result) {
            app.toast.error('No result returned for this device');
            return null;
        }
        return result;
    }

    async onActionCheckUpdate() {
        const app = this.getApp();
        const result = await this._dryRun();
        if (!result) return;

        if (result.action === 'would_send') {
            const build = result.build ? ` (build #${result.build})` : '';
            const relay = result.via === 'terminal' ? ' — delivered through the paired kiosk terminal' : '';
            await app.modal.alert(`Update available: v${result.version || '?'}${build}${relay}`, 'Update Available', { type: 'info' });
        } else if (result.action === 'would_queue') {
            await app.modal.alert(
                `Device is offline — an update to v${result.version || '?'} (newest matching build at reconnect) can be queued.`,
                'Update Available',
                { type: 'info' }
            );
        } else if (result.action === 'skipped') {
            await app.modal.alert(
                SKIP_MESSAGES[result.reason] || `Skipped: ${result.reason || 'unknown'}`,
                'No Update',
                { type: result.reason === 'current' ? 'success' : 'warning' }
            );
        } else {
            app.toast.error('Device not found');
        }
    }

    async onActionUpdateFirmware() {
        const app = this.getApp();
        const result = await this._dryRun();
        if (!result) return;

        let force = false;
        if (result.action === 'skipped') {
            // force bypasses only current and pending; everything else is a hard stop
            if (result.reason === 'current' || result.reason === 'pending') {
                let msg = result.reason === 'current'
                    ? 'Device reports it is already up to date. Force a reinstall anyway?'
                    : 'An update was already dispatched recently (10-minute cooldown). Force a new dispatch anyway?';
                // A forced update on an offline device is queued, not sent now —
                // say so, but only when the server can actually queue it.
                if (!this.model.get('is_online') && this._canQueue) {
                    msg += ' The device is offline — the forced update will be queued and fire when it reconnects.';
                }
                const confirmed = await Dialog.confirm({
                    title: 'Force Update?',
                    message: msg,
                    confirmText: 'Force Update',
                    confirmClass: 'btn-warning',
                });
                if (!confirmed) return;
                force = true;
            } else {
                await app.modal.alert(
                    SKIP_MESSAGES[result.reason] || `Skipped: ${result.reason || 'unknown'}`,
                    'Cannot Update',
                    { type: 'warning' }
                );
                return;
            }
        } else if (result.action === 'would_send') {
            const relay = result.via === 'terminal'
                ? ' It will be delivered through the paired kiosk terminal.' : '';
            const confirmed = await Dialog.confirm({
                title: 'Update Firmware',
                message: `Update this device to v${result.version || '?'}?${relay}`,
                confirmText: 'Update',
                confirmClass: 'btn-primary',
            });
            if (!confirmed) return;
        } else if (result.action === 'would_queue') {
            // Offline target on a queue-aware server: offer to queue instead of the
            // old hard-stop alert. "currently vX" — the build resolves at dispatch.
            const confirmed = await Dialog.confirm({
                title: 'Device Offline — Queue Update?',
                message: `This device is offline. Queue an update so the newest matching build (currently v${result.version || '?'}) installs automatically when it reconnects?`,
                confirmText: 'Queue Update',
                confirmClass: 'btn-warning',
            });
            if (!confirmed) return;
        } else {
            app.toast.error('Device not found');
            return;
        }

        const response = await app.rest.POST('/api/firmware/ota/trigger', {
            device: this.model.get('id'),
            // Scope-only group so a member-tier manage_firmware grant resolves
            // (see _dryRun); the device id is the dispatch target.
            group: this.model.get('group.id'),
            force,
            // Only send the flag once the dry-run proved the server supports it,
            // so the dominant online path never risks unknown-field handling on an
            // old server. On an offline target this is what turns the skip into a
            // persisted queued intent.
            ...(this._canQueue ? { queue: true } : {}),
        });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to trigger update');
            return;
        }
        const sent = response.data.data?.results?.[0];
        if (sent?.action === 'sent') {
            this._activeAttempt = sent.attempt ?? null;
            this._lastToastedAttempt = null;
            this._showDispatched(sent.via);
            // Dispatch accepted — NOT a completed update. The real outcome
            // (success/failed/rolled_back) arrives over device:ota_progress and
            // toasts from _updateProgress. Firing success here would lie when the
            // OTA immediately fails (e.g. url_scheme).
            const relay = sent.via === 'terminal' ? ' via the kiosk terminal' : '';
            app.toast.info(`Update to v${sent.version || '?'} dispatched${relay} — waiting for device…`);
            this.attemptsView?.collection?.fetch();
        } else if (sent?.action === 'queued') {
            // Queueing succeeded; the update's own outcome still arrives over WS
            // when it auto-dispatches on reconnect (no lying success toast). Also
            // catches a device that dropped offline between dry-run and trigger.
            app.toast.info('Update queued — the newest matching build will install when the device reconnects');
            this._refreshQueued();
            this.attemptsView?.collection?.fetch();
        } else if (sent?.action === 'skipped') {
            app.toast.warning(SKIP_MESSAGES[sent.reason] || `Skipped: ${sent.reason || 'unknown'}`);
        } else {
            app.toast.error('Unexpected trigger response');
        }
    }

    // Fetch the live queued row for this device (status=queued, newest) as its
    // own 1-row list. Sets this._queued to the row or null. A failed fetch
    // degrades to no banner (Collection.fetch resolves rather than throws).
    async _loadQueued() {
        try {
            const list = new FirmwareUpdateList({
                params: { device: this.model.get('id'), status: 'queued', size: 1, sort: '-created' },
            });
            await list.fetch();
            this._queued = list.models?.[0] || null;
        } catch {
            this._queued = null;
        }
    }

    // Re-fetch queued state and re-render the banner. Never call from
    // onAfterRender/onAfterMount (would loop) — only from action handlers, the
    // reconnect listener, and the post-terminal refresh.
    async _refreshQueued() {
        await this._loadQueued();
        await this.render();
    }

    async onActionCancelQueued() {
        const app = this.getApp();
        if (!this._queued) return;
        const confirmed = await Dialog.confirm({
            title: 'Cancel Queued Update?',
            message: 'Stop the update that is waiting to install when this device reconnects?',
            confirmText: 'Cancel Update',
            confirmClass: 'btn-danger',
        });
        if (!confirmed) return;
        const ok = await cancelQueuedUpdate(app, this._queued.get('id'), this.model.get('group.id'));
        if (ok) {
            await this._refreshQueued();
            this.attemptsView?.collection?.fetch();
        }
    }

    // Re-apply the last live progress after any re-render (banner render, queued
    // clear, terminal refresh). Pure DOM work — no fetching, per repo rules — so
    // an event that landed during the async render window isn't lost. Skip
    // terminal events: the post-terminal refresh hides the bar, and the toast is
    // the lasting signal.
    onAfterRender() {
        if (this._lastProgress && !TERMINAL_STATUSES.includes(this._lastProgress.status)) {
            this._updateProgress(this._lastProgress);
        }
    }

    // No `sent` event ever arrives over WS — the trigger response is the only
    // signal until the device reports `started`.
    _showDispatched(via) {
        const wrap = this._el('ota-progress');
        if (!wrap) return;
        wrap.classList.remove('d-none');
        const label = via === 'terminal'
            ? 'Dispatched via kiosk terminal — staging…' : STATUS_LABELS.sent;
        this._setText('ota-status-label', label);
        this._setText('ota-percent-label', '');
        const bar = this._el('ota-bar');
        if (bar) {
            bar.className = 'progress-bar progress-bar-striped progress-bar-animated';
            bar.style.width = '4%';
        }
        this._el('ota-error')?.classList.add('d-none');
    }

    _updateProgress(e) {
        const wrap = this._el('ota-progress');
        if (!wrap) return;
        wrap.classList.remove('d-none');

        const terminal = TERMINAL_STATUSES.includes(e.status);
        const version = e.version ? ` v${e.version}` : '';
        this._setText('ota-status-label', `${STATUS_LABELS[e.status] || e.status}${version}`);
        this._setText('ota-percent-label', (e.percent === null || e.percent === undefined) ? '' : `${e.percent}%`);

        const bar = this._el('ota-bar');
        if (bar) {
            const color = { success: 'bg-success', failed: 'bg-danger', rolled_back: 'bg-warning' }[e.status] || '';
            bar.className = `progress-bar ${terminal ? color : 'progress-bar-striped progress-bar-animated'}`.trim();
            bar.style.width = `${e.status === 'success' ? 100 : (e.percent || 0)}%`;
        }

        const errEl = this._el('ota-error');
        if (errEl) {
            errEl.textContent = e.error || '';
            errEl.classList.toggle('d-none', !e.error);
        }

        if (terminal) {
            // Persistent, honest outcome signal — the progress bar is hidden by the
            // refresh below, so the toast is what the user is left with. Dedupe per
            // attempt so a replayed terminal event doesn't double-toast.
            const attempt = e.attempt ?? this._activeAttempt;
            if (attempt == null || attempt !== this._lastToastedAttempt) {
                this._lastToastedAttempt = attempt;
                const app = this.getApp();
                const version = e.version ? ` v${e.version}` : '';
                if (e.status === 'success') {
                    app.toast.success(`Firmware update complete${version}`);
                } else if (e.status === 'rolled_back') {
                    app.toast.warning(`Firmware update rolled back${version}`);
                } else {
                    app.toast.error(`Firmware update failed${e.error ? `: ${e.error}` : ''}`);
                }
            }
            this._activeAttempt = null;
            if (this._terminalTimer) clearTimeout(this._terminalTimer);
            this._terminalTimer = setTimeout(() => this._refreshAfterTerminal(), 1200);
        }
    }

    async _refreshAfterTerminal() {
        this._terminalTimer = null;
        try {
            await this.model.fetch();
            await this._loadQueued();
            this.attemptsView?.collection?.fetch();
            await this.render();
        } catch { /* best effort */ }
    }

    _el(bind) { return this.el?.querySelector(`[data-bind="${bind}"]`); }
    _setText(bind, text) { const el = this._el(bind); if (el) el.textContent = text || ''; }

    onBeforeDestroy() {
        if (this._onOtaProgress) {
            this.getApp().events.off('device:ota_progress', this._onOtaProgress);
        }
        if (this._onDeviceStateChanged) {
            this.getApp().events.off('device:state_changed', this._onDeviceStateChanged);
        }
        if (this._terminalTimer) clearTimeout(this._terminalTimer);
        if (this._reconnectTimer) clearTimeout(this._reconnectTimer);
    }
}

export default DeviceFirmwareView;
